import { isAxiosError } from "helpers/utils.helper"
import { sanitizeFileNamePart, stripPdfExtension } from "helpers/fileName.helper"
import { RESULT_CSV_MESSAGES, RESULT_CSV_NAME_PART_MAX_LENGTH } from "helpers/resultCsv.constants"

const sanitizeNamePart = (value: string) => sanitizeFileNamePart(value, RESULT_CSV_NAME_PART_MAX_LENGTH)

// Composes the download name for the on-demand result CSV (REQ-420):
// `<original PDF>__quotation_<quotation name>.csv`.
//
// Deliberately built here rather than read from the response's Content-Disposition. The backend's
// name is `quotation_<id>_result.csv` — correct but opaque, since a user recognises their drawing and
// quotation by name, not by database id. Downside to accept: if the backend ever changes its naming,
// this will not follow it.
//
// `quotationName` is required, with no unnamed case to handle: the API never returns a null name —
// `Quotations.to_dict()` substitutes "Untitled" — so a quotation always has one from here.
//
// `drawingName` is genuinely optional: its query can still be in flight on the first paint after the
// drawer opens, or have failed. Permissions are not a factor — the drawing endpoint carries the same
// `IsOwner | IsSuperAdminOrAdmin` as the CSV endpoint, so anyone offered this control can read it.
export const buildResultCsvFileName = ({
	quotationName,
	drawingName,
}: {
	quotationName: string
	drawingName?: string | null
}): string => {
	const drawingPart = drawingName ? sanitizeNamePart(stripPdfExtension(drawingName)) : ""
	const quotationPart = sanitizeNamePart(quotationName)

	return drawingPart ? `${drawingPart}__quotation_${quotationPart}.csv` : `quotation_${quotationPart}.csv`
}

// Maps the endpoint's documented status codes to short, actionable copy. Deliberately does NOT go
// through `useApiErrorFormatter`: this request is `responseType: "blob"`, so a 4xx body arrives as
// a Blob and that formatter reads `response.data` as a plain object — `Object.entries(blob)` is
// empty, so it would silently surface no message at all.
export const getResultCsvErrorMessage = (error: unknown): string => {
	if (!isAxiosError(error)) return RESULT_CSV_MESSAGES.failed
	// Axios reports both its own client timeout and a dropped connection as ECONNABORTED.
	if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return RESULT_CSV_MESSAGES.timedOut

	switch (error.response?.status) {
		// There is no global 401 interceptor, so this has to say "signed out" itself — otherwise an
		// expired token reads as a generation failure and invites an endless retry.
		case 401:
			return RESULT_CSV_MESSAGES.unauthorized
		case 403:
			return RESULT_CSV_MESSAGES.forbidden
		case 404:
			return RESULT_CSV_MESSAGES.notFound
		case 409:
			return RESULT_CSV_MESSAGES.submitted
		default:
			return RESULT_CSV_MESSAGES.failed
	}
}
